/**
 * <DenseTable> — 13-ui-screens.md §3.8.
 *
 * A semantic `<table>` with `table-layout: fixed` inside its own
 * `overflow-x: auto` wrapper. Used where antd `<Table>` structurally cannot
 * go: the jobs grid (an absolutely-positioned popover anchored inside a cell,
 * `vertical-align: top` rows), the crew grid (columns are DYNAMIC — one per
 * job line — with two-line computed headers), the casual timesheet (a totals
 * row inside `<tbody>` with its own background), and the expiry bands
 * (headerless, banded, four independent tables).
 *
 * No sorting, no pagination, no selection. If you want those, use antd Table.
 *
 * §3.9: the page body must NEVER scroll horizontally — the wrapper does.
 *
 * RESIZABLE COLUMNS (§7.5 [N], client request 13 Oct 2026) are opt-in: pass `resize` from
 * `useResizableColumns` and mark columns `resizable`. Each such header gets a draggable right
 * edge; once a width is stored the table is the SUM of its columns rather than `100%` —
 * `lib/column-widths.ts` says why the layout freezes instead of stretching.
 *
 * A LOCKED HEADER AND FROZEN COLUMNS (client request 14 Oct 2026, `lib/table-freeze.ts`) are
 * opt-in too. `stickyHeader` gives the table its own scroll box, one screen tall less the top bar,
 * so the header row (`position: sticky; top: 0`) holds while the rows scroll — the Attendance
 * board's fix, which says why the box needs a height. `freezeColumns={n}` pins the first `n`
 * columns while the rest scroll sideways. Body rows are the CALLER's markup, so the frozen cells
 * are found after render rather than styled by `<DenseCell>`: `useFrozenCells` walks every row,
 * counting `colSpan` (a totals row's first cell often spans two columns, which `nth-child` would
 * miscount), and pins each cell that STARTS inside the frozen block at the summed width of the
 * columns before it. A pinned cell must be opaque or the scrolled cells show through, so one
 * without its own background borrows its row's, else the card's.
 */
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { frozenTableWidth, type ResizableColumns } from '@/lib/column-widths';
import { pageZoom } from '@/lib/text-size';
import { ColumnResizeHandle } from './ColumnResizeHandle';

export interface ColSpec {
  key: string;
  /** Line 1 of the header. Omit entirely for a headerless band table. */
  title?: ReactNode;
  /** Line 2 — the computed sub-header (`48 mt`, `hours`, `J1 only`). */
  subTitle?: ReactNode;
  width?: number | string;
  align?: 'left' | 'center' | 'right';
  /** Columns 3+ of the crew grid carry a left hairline. */
  divider?: boolean;
  /** Give the header a draggable right edge. Needs `resize` on the table and a `title`. */
  resizable?: boolean;
}

export interface DenseTableProps {
  columns: ColSpec[];
  children: ReactNode;
  /** Render no `<thead>` at all — the document-expiry bands. */
  headerless?: boolean;
  /** Minimum table width; the wrapper scrolls when the viewport is narrower. Ignored while a
   *  dragged layout is stored — the table is then exactly as wide as its columns. */
  minWidth?: number;
  /** `useResizableColumns(...)` — enables the `resizable` columns' header edges. */
  resize?: ResizableColumns;
  /** Lock the header row: the table scrolls in its own box, one screen tall less the top bar. */
  stickyHeader?: boolean;
  /** Pin the first `n` columns while the rest scroll sideways (`useFrozenColumns`). 0 = none. */
  freezeColumns?: number;
}

/** One screen less the top bar and a little air — the Attendance board's box (`-AttendanceGrid`).
 *  In a full view (`FullView.tsx`) the height it measured below the box wins. */
export const STICKY_BOX_MAX_HEIGHT = 'var(--cm-fv-avail, calc(var(--cm-viewport-h) - var(--cm-header-h) - 24px))';

export function DenseTable({
  columns,
  children,
  headerless = false,
  minWidth,
  resize,
  stickyHeader = false,
  freezeColumns = 0,
}: DenseTableProps) {
  const stored = resize?.widths ?? null;
  const widthOf = (c: ColSpec) => (c.resizable && stored?.[c.key] !== undefined ? stored[c.key] : c.width);
  const frozen = stored === null ? null : frozenTableWidth(columns.map(widthOf));
  const ownRef = useRef<HTMLTableElement | null>(null);
  useFrozenCells(ownRef, Math.min(freezeColumns, columns.length), stickyHeader);
  return (
    <div
      className="cm-scroll"
      /* `FullView.tsx` measures from the top of this box. */
      data-cm-sticky-box={stickyHeader ? '' : undefined}
      style={
        stickyHeader
          ? { overflow: 'auto', width: '100%', maxHeight: STICKY_BOX_MAX_HEIGHT }
          : { overflowX: 'auto', width: '100%' }
      }
    >
      <table
        ref={(el) => {
          ownRef.current = el;
          if (resize) resize.tableRef.current = el;
        }}
        style={{
          width: frozen ?? '100%',
          minWidth: frozen === null ? minWidth : undefined,
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          {columns.map((c) => (
            <col
              key={c.key}
              data-col-key={resize && c.resizable ? c.key : undefined}
              style={{ width: widthOf(c) }}
            />
          ))}
        </colgroup>
        {headerless ? null : (
          <thead>
            <tr style={{ background: 'var(--surface-subtle)' }}>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  style={{
                    /* Sticky beats relative: a sticky cell is still a containing block, so the
                     * resize handle stays on its edge. Its own fill, because the row's does not
                     * paint behind a cell that has left it. */
                    position: stickyHeader ? 'sticky' : resize && c.resizable ? 'relative' : undefined,
                    top: stickyHeader ? 0 : undefined,
                    zIndex: stickyHeader ? 3 : undefined,
                    background: stickyHeader ? 'var(--surface-subtle)' : undefined,
                    textAlign: c.align ?? 'left',
                    padding: c.subTitle ? '5px 6px' : '7px 12px',
                    borderBottom: '1px solid var(--border-subtle)',
                    borderLeft: c.divider ? '1px solid var(--neutral-100)' : undefined,
                    fontFamily: 'var(--font-display)',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '.14em',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <span style={{ display: 'block' }}>{c.title}</span>
                  {c.subTitle ? (
                    <span
                      style={{
                        display: 'block',
                        marginTop: 2,
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        fontWeight: 400,
                        letterSpacing: 0,
                        textTransform: 'none',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {c.subTitle}
                    </span>
                  ) : null}
                  {resize && c.resizable ? (
                    <ColumnResizeHandle
                      label={typeof c.title === 'string' ? c.title : c.key}
                      onPointerDown={(e) => resize.startDrag(c.key, e)}
                      onReset={resize.reset}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** A body row with the prototype's `.cw-row` hover wash. */
export function DenseRow({
  children,
  onClick,
  selected,
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  selected?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <tr
      className="cw-row"
      onClick={onClick}
      style={{
        borderBottom: '1px solid var(--neutral-100)',
        cursor: onClick ? 'pointer' : undefined,
        background: selected ? 'var(--green-050)' : undefined,
        ...style,
      }}
    >
      {children}
    </tr>
  );
}

export function DenseCell({
  children,
  align = 'left',
  divider,
  padding = '6px 10px',
  colSpan,
  style,
}: {
  children?: ReactNode;
  align?: 'left' | 'center' | 'right';
  divider?: boolean;
  padding?: string;
  colSpan?: number;
  style?: React.CSSProperties;
}) {
  return (
    <td
      colSpan={colSpan}
      style={{
        textAlign: align,
        padding,
        verticalAlign: 'top',
        borderLeft: divider ? '1px solid var(--neutral-100)' : undefined,
        ...style,
      }}
    >
      {children}
    </td>
  );
}

/**
 * Pins every cell that starts inside the first `count` columns: `position: sticky`, `left` = the
 * summed widths of the columns before it, above the scrolled cells (a header cell above both),
 * a hairline on the frozen block's right edge, and an opaque fill. Runs after EVERY render — the
 * rows are the caller's and change with each page, edit and refetch — and on window resize,
 * which moves an unsized column's width. Cells it pinned on a previous pass are released first,
 * so lowering the count, or a row that moved, leaves nothing stuck behind.
 *
 * It writes the DOM behind React's back on purpose and only ever touches what it set: the four
 * properties below, whose ORIGINAL inline values it keeps in `data-frozen` and puts back on
 * release — a sticky header cell already carries React's `position: sticky; z-index: 3`, and
 * removing them would unstick the header — and the background only when the cell had none of its
 * own (`data-frozen-bg`).
 *
 * Exported for the day sheet's crew grid, a hand-rolled `<table>` (its two header rows and roving
 * focus do not fit `<DenseTable>`), which pins its leading columns the same way.
 */
export function useFrozenCells(
  tableRef: React.RefObject<HTMLTableElement | null>,
  count: number,
  stickyHeader: boolean,
): void {
  const apply = useRef<() => void>(() => undefined);
  apply.current = () => {
    const table = tableRef.current;
    if (!table) return;
    for (const cell of Array.from(table.querySelectorAll<HTMLTableCellElement>('[data-frozen]'))) {
      const saved = JSON.parse(cell.getAttribute('data-frozen') || '{}') as Record<string, string>;
      for (const prop of PINNED_PROPS) {
        if (saved[prop]) cell.style.setProperty(prop, saved[prop]);
        else cell.style.removeProperty(prop);
      }
      if (cell.hasAttribute('data-frozen-bg')) cell.style.removeProperty('background-color');
      cell.removeAttribute('data-frozen');
      cell.removeAttribute('data-frozen-bg');
    }

    /* A keyboard grid (the timesheet, the statutory grid) moves focus with ↑/↓ and Enter, and
     * the browser scrolls a focused input just into view — UNDER the locked header or the frozen
     * block, where it cannot be seen. Scroll padding the size of each keeps it clear of both. */
    const box = table.parentElement;
    const zoomNow = pageZoom();
    if (box && stickyHeader) {
      box.style.scrollPaddingTop = `${(table.tHead?.getBoundingClientRect().height ?? 0) / zoomNow}px`;
    }
    if (count <= 0) {
      box?.style.removeProperty('scroll-padding-left');
      return;
    }

    /* Column widths from the bottom header row, one cell per column (no header colSpans). A
     * visual pixel under the text-size zoom, so divided back to CSS pixels for `left`. */
    const headRows = table.tHead?.rows;
    const headRow = headRows && headRows.length > 0 ? headRows[headRows.length - 1] : null;
    const zoom = pageZoom();
    const widths = headRow
      ? Array.from(headRow.cells).map((c) => c.getBoundingClientRect().width / zoom)
      : Array.from(table.rows[0]?.cells ?? []).map((c) => c.getBoundingClientRect().width / zoom);
    const offsets: number[] = [];
    let sum = 0;
    for (let i = 0; i < count; i++) {
      offsets.push(sum);
      sum += widths[i] ?? 0;
    }
    box?.style.setProperty('scroll-padding-left', `${sum}px`);

    for (const row of Array.from(table.rows)) {
      const inHead = row.parentElement?.tagName === 'THEAD';
      const rowBg = getComputedStyle(row).backgroundColor;
      let col = 0;
      for (const cell of Array.from(row.cells)) {
        const start = col;
        col += cell.colSpan || 1;
        if (start >= count) break;
        const saved: Record<string, string> = {};
        for (const prop of PINNED_PROPS) saved[prop] = cell.style.getPropertyValue(prop);
        cell.setAttribute('data-frozen', JSON.stringify(saved));
        cell.style.position = 'sticky';
        cell.style.left = `${offsets[start]}px`;
        /* 2, not 1: antd's Select placeholder is `z-index: 1` (the day sheet's Product and
         * Customer cells), and at a tie the later cell in the DOM paints over the frozen one.
         * So the locked header is 3 and a frozen header cell 4, staying above both. */
        cell.style.zIndex = inHead ? '4' : '2';
        if (col >= count) cell.style.boxShadow = 'inset -1px 0 0 var(--border-default)';
        if (isTransparent(getComputedStyle(cell).backgroundColor)) {
          cell.setAttribute('data-frozen-bg', '');
          cell.style.backgroundColor = isTransparent(rowBg)
            ? inHead
              ? 'var(--surface-subtle)'
              : 'var(--surface-card)'
            : rowBg;
        }
      }
    }
  };

  useLayoutEffect(() => {
    apply.current();
  });
  useLayoutEffect(() => {
    const onResize = () => apply.current();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
}

/** What `useFrozenCells` sets on a pinned cell, and restores on release. */
const PINNED_PROPS = ['position', 'left', 'z-index', 'box-shadow'] as const;

function isTransparent(color: string): boolean {
  return color === 'transparent' || color === 'rgba(0, 0, 0, 0)';
}
